import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const fmtMXN = (n) => `$${Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

const changeTypeConfig = {
  added:    { label: 'Agregado',   color: '#10B981', bg: '#D1FAE5', arrow: '＋' },
  removed:  { label: 'Eliminado',  color: '#EF4444', bg: '#FEE2E2', arrow: '－' },
  modified: { label: 'Modificado', color: '#F59E0B', bg: '#FEF3C7', arrow: '≠' },
};

const BudgetVersions = ({ budgetId, isNotary, onRestored, onVersionSaved }) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savingSummary, setSavingSummary] = useState('');
  const [savingVersion, setSavingVersion] = useState(false);
  const [versions, setVersions]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selected, setSelected]         = useState([]);   // hasta 2 IDs para comparar
  const [diff, setDiff]                 = useState(null);
  const [diffLoading, setDiffLoading]   = useState(false);
  const [restoring, setRestoring]       = useState(null); // version_number en proceso
  const [detail, setDetail]             = useState(null); // versión expandida

  useEffect(() => {
    fetchVersions();
  }, [budgetId]);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/budgets/${budgetId}/versions`);
      setVersions(res.data);
    } catch {
      toast.error('Error al cargar versiones');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setDiff(null);
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 2)  return [prev[1], id];
      return [...prev, id];
    });
  };

  const fetchDiff = async () => {
    if (selected.length !== 2) return;
    setDiffLoading(true);
    try {
      const [a, b] = selected;
      const res = await api.get(`/budgets/${budgetId}/versions/diff`, { params: { a, b } });
      setDiff(res.data);
    } catch {
      toast.error('Error al comparar versiones');
    } finally {
      setDiffLoading(false);
    }
  };
    const handleSaveVersion = async () => {
        if (!savingSummary.trim()) {
            toast.error('Escribe una descripción del cambio');
            return;
        }
        setSavingVersion(true);
        try {
            await api.post(`/budgets/${budgetId}/versions`, {
            change_summary: savingSummary.trim(),
            });
            toast.success('Versión guardada');
            setSavingSummary('');
            setShowSaveModal(false);
            fetchVersions();
            if (onVersionSaved) onVersionSaved(); // apaga el indicador en BudgetDetail
        } catch {
            toast.error('Error al guardar versión');
        } finally {
            setSavingVersion(false);
        }
    };

  const handleRestore = async (version) => {
    toast((t) => (
        <div>
        <p className="mb-2" style={{ fontSize: 13 }}>
            ¿Restaurar a la <strong>versión #{version.version_number}</strong>?<br />
            <span style={{ color: '#6B7280', fontSize: 12 }}>Se creará una versión nueva con ese contenido.</span>
        </p>
        <div className="d-flex" style={{ gap: 8 }}>
            <button
            className="btn btn-warning btn-sm"
            onClick={async () => {
                toast.dismiss(t.id);
                setRestoring(version.version_number);
                try {
                await api.post(`/budgets/${budgetId}/versions/${version.id}/restore`);
                toast.success(`Presupuesto restaurado a versión #${version.version_number}`);
                setSelected([]);
                setDiff(null);
                setDetail(null);
                fetchVersions();
                if (onRestored) onRestored();
                } catch (e) {
                toast.error(e.response?.data?.message || 'Error al restaurar');
                } finally {
                setRestoring(null);
                }
            }}
            >
            Sí, restaurar
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => toast.dismiss(t.id)}>
            Cancelar
            </button>
        </div>
        </div>
    ), { duration: Infinity });
    };

  if (loading) return <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"></div></div>;

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
            <h6 className="mb-0">Historial de versiones</h6>
            <small className="text-muted">{versions.length} versión{versions.length !== 1 ? 'es' : ''} guardada{versions.length !== 1 ? 's' : ''}</small>
        </div>
        <div className="d-flex" style={{ gap: 8 }}>
            {selected.length === 2 && (
            <button className="btn btn-sm btn-outline-primary" onClick={fetchDiff} disabled={diffLoading}>
                {diffLoading
                ? <><span className="spinner-border spinner-border-sm mr-1"></span>Comparando...</>
                : <><i className="ti-exchange-vertical mr-1"></i>Comparar</>}
            </button>
            )}
            {selected.length === 1 && (
            <small className="text-muted d-flex align-items-center">Selecciona otra para comparar</small>
            )}
            <button
            className="btn btn-sm btn-warning"
            onClick={() => setShowSaveModal(true)}
            >
            <i className="ti-save mr-1"></i> Guardar versión
            </button>
        </div>
        </div>

        {/* Modal guardar versión */}
        {showSaveModal && (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
            <div style={{
            backgroundColor: '#fff', borderRadius: 12, padding: 28,
            maxWidth: 440, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}>
            <h6 className="mb-1">Guardar versión del presupuesto</h6>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                Describe qué cambios incluye esta versión.
            </p>
            <input
                type="text"
                className="form-control mb-3"
                placeholder="Ej: Ajuste de honorarios y eliminación de retención ISR"
                value={savingSummary}
                onChange={e => setSavingSummary(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSaveVersion()}
                autoFocus
            />
            <div className="d-flex justify-content-end" style={{ gap: 10 }}>
                <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => { setShowSaveModal(false); setSavingSummary(''); }}
                disabled={savingVersion}
                >
                Cancelar
                </button>
                <button
                className="btn btn-warning btn-sm"
                onClick={handleSaveVersion}
                disabled={savingVersion || !savingSummary.trim()}
                >
                {savingVersion ? 'Guardando...' : 'Guardar versión'}
                </button>
            </div>
            </div>
        </div>
        )}

      {/* Lista de versiones */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {versions.map(v => {
          const isSelected = selected.includes(v.id);
          const isExpanded = detail?.id != null && String(detail.id) === String(v.id);

          return (
            <div key={v.id}
              style={{
                border: `2px solid ${isSelected ? '#5066E1' : '#E8E6E0'}`,
                borderRadius: 8,
                background: isSelected ? '#EEF0FB' : '#fff',
                transition: 'border-color 0.15s, background 0.15s',
              }}>

              {/* Fila principal */}
              <div
                className="d-flex align-items-center"
                style={{ padding: '10px 14px', gap: 10, cursor: 'pointer' }}
                onClick={() => toggleSelect(v.id)}
              >
                {/* Checkbox visual */}
                <div style={{
                  width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                  border: `2px solid ${isSelected ? '#5066E1' : '#ccc'}`,
                  background: isSelected ? '#5066E1' : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {isSelected && <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>✓</span>}
                </div>

                {/* Badge versión */}
                <span style={{
                  background: v.is_current ? '#1a1a2e' : '#F3F4F6',
                  color: v.is_current ? '#C9A84C' : '#6B7280',
                  borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700, flexShrink: 0,
                }}>
                  v{v.version_number}
                  {v.is_current && <span style={{ marginLeft: 4, fontSize: 10 }}>● actual</span>}
                </span>

                {/* Resumen */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {v.change_summary}
                  </div>
                  <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                    {v.created_by} · {v.created_at}
                  </div>
                </div>

                {/* Total */}
                <span style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e', flexShrink: 0 }}>
                  {fmtMXN(v.grand_total)}
                </span>

                {/* Botón expandir */}
                <button
                    className="btn btn-sm btn-outline-secondary"
                    style={{ padding: '2px 8px', fontSize: 11 }}
                  onClick={e => {
                        e.stopPropagation();
                        if (isExpanded) {
                            setDetail(null);
                        } else {
                            setDetail(null);
                            api.get(`/budgets/${budgetId}/versions/${v.id}/detail`)
                            .then(res => setDetail(res.data))
                            .catch(() => toast.error('Error al cargar detalle'));
                        }
                        }}
                    >
                    {isExpanded ? 'Ocultar' : 'Ver detalle'}
                    </button>

                {/* Botón restaurar (solo notario, no en versión actual) */}
                {isNotary && !v.is_current && (
                  <button
                    className="btn btn-sm btn-outline-warning"
                    style={{ padding: '2px 8px', fontSize: 11 }}
                    disabled={restoring === v.version_number}
                    onClick={e => { 
                        e.stopPropagation(); 
                        if (isExpanded) {
                            setDetail(null);
                        } else {
                            // Cargar el snapshot completo
                            api.get(`/budgets/${budgetId}/versions/${v.id}`)
                            .then(res => {
                                console.log('version detail:', res.data);
                                setDetail(res.data);
                            })
                            .catch(() => toast.error('Error al cargar detalle'));
                        }
                        }}
                    >
                    {restoring === v.version_number
                      ? <span className="spinner-border spinner-border-sm"></span>
                      : '↩ Restaurar'}
                  </button>
                )}
              </div>

              {/* Detalle expandido */}
              {isExpanded && detail?.snapshot && (
                <VersionDetail snapshot={detail.snapshot} diff={detail.diff} />
              )}
            </div>
          );
        })}
      </div>

      {/* Panel de comparación */}
      {diff && (
        <DiffPanel
          diff={diff}
          vA={versions.find(v => v.id === selected[0])}
          vB={versions.find(v => v.id === selected[1])}
          onClose={() => setDiff(null)}
        />
      )}
    </div>
  );
};

// ── Detalle de una versión ───────────────────────────────────────────────────
const VersionDetail = ({ snapshot, diff }) => {
  if (!snapshot) return <div className="p-3 text-muted small">Sin datos</div>;

  const fmtMXN = (n) => `$${Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
  const items  = snapshot?.items ?? [];
  const visible = items.filter(i => i.type !== 'informative');

  // Indexar cambios por nombre para lookup rápido
  const changesMap = {};
  if (diff?.changes) {
    diff.changes.forEach(c => { changesMap[c.name] = c; });
  }

  return (
    <div style={{ borderTop: '1px solid #E8E6E0', padding: '12px 14px', background: '#FAFAFA' }}>

      {/* Resumen de cambios si hay diff */}
      {diff && (
        <div className="mb-3 d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
          <small className="text-muted font-weight-bold">Cambios vs versión anterior:</small>
          {diff.changes.length === 0 ? (
            <span className="badge badge-secondary" style={{ fontSize: 11 }}>Sin cambios en conceptos</span>
          ) : (
            <>
              {diff.changes.filter(c => c.type === 'added').length > 0 && (
                <span className="badge badge-success" style={{ fontSize: 11 }}>
                  +{diff.changes.filter(c => c.type === 'added').length} agregado{diff.changes.filter(c => c.type === 'added').length !== 1 ? 's' : ''}
                </span>
              )}
              {diff.changes.filter(c => c.type === 'removed').length > 0 && (
                <span className="badge badge-danger" style={{ fontSize: 11 }}>
                  -{diff.changes.filter(c => c.type === 'removed').length} eliminado{diff.changes.filter(c => c.type === 'removed').length !== 1 ? 's' : ''}
                </span>
              )}
              {diff.changes.filter(c => c.type === 'modified').length > 0 && (
                <span className="badge badge-warning" style={{ fontSize: 11 }}>
                  {diff.changes.filter(c => c.type === 'modified').length} modificado{diff.changes.filter(c => c.type === 'modified').length !== 1 ? 's' : ''}
                </span>
              )}
              <span className="ml-2" style={{ fontSize: 12, fontWeight: 700, color: diff.diff_total >= 0 ? '#10B981' : '#EF4444' }}>
                {diff.diff_total >= 0 ? '+' : ''}{fmtMXN(diff.diff_total)} vs anterior
              </span>
            </>
          )}
        </div>
      )}

      {!diff && (
        <div className="mb-3">
          <span className="badge badge-secondary" style={{ fontSize: 11 }}>Versión inicial</span>
        </div>
      )}

      {/* Tabla de conceptos con resaltado */}
      <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>Concepto</th>
            <th>Tipo</th>
            <th className="text-right">Monto</th>
            <th className="text-right">Total línea</th>
            <th style={{ width: 90 }}>Cambio</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((item, i) => {
            const change = changesMap[item.name];
            let rowBg    = 'transparent';
            let changeBadge = null;

            if (change) {
              if (change.type === 'added') {
                rowBg = '#F0FDF4';
                changeBadge = <span className="badge badge-success" style={{ fontSize: 10 }}>Nuevo</span>;
              } else if (change.type === 'removed') {
                rowBg = '#FEF2F2';
                changeBadge = <span className="badge badge-danger" style={{ fontSize: 10 }}>Eliminado</span>;
              } else if (change.type === 'modified') {
                rowBg = '#FFFBEB';
                const delta = (change.amount_b ?? 0) - (change.amount_a ?? 0);
                changeBadge = (
                  <span style={{ fontSize: 11, fontWeight: 700, color: delta >= 0 ? '#10B981' : '#EF4444' }}>
                    {delta >= 0 ? '▲' : '▼'} {fmtMXN(Math.abs(delta))}
                  </span>
                );
              }
            }

            return (
              <tr key={i} style={{ background: rowBg }}>
                <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.name}
                </td>
                <td>
                  <span className={`badge ${
                    item.type === 'normal' ? 'badge-primary' :
                    item.type === 'discount' ? 'badge-danger' : 'badge-secondary'
                  }`} style={{ fontSize: 10 }}>
                    {item.type === 'normal' ? 'Costo' : item.type === 'discount' ? 'Descuento' : 'Info'}
                  </span>
                </td>
                <td className="text-right">{fmtMXN(item.amount)}</td>
                <td className="text-right font-weight-bold">
                  {item.type === 'discount'
                    ? <span className="text-danger">-{fmtMXN(item.amount)}</span>
                    : fmtMXN(item.amount + (item.iva_amount ?? 0))}
                </td>
                <td>{changeBadge}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} className="text-right font-weight-bold">Total</td>
            <td className="text-right font-weight-bold">{fmtMXN(snapshot.grand_total)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ── Panel de comparación ─────────────────────────────────────────────────────
const DiffPanel = ({ diff, vA, vB, onClose }) => {
  const fmtMXN = (n) => n == null ? '—' : `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
  const diffTotal = diff.diff_total;

  return (
    <div style={{
      marginTop: 16,
      border: '2px solid #5066E1',
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        background: '#1a1a2e', color: '#fff',
        padding: '10px 16px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>
          Comparación: v{vA?.version_number} → v{vB?.version_number}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: '#C9A84C', fontWeight: 700 }}>
            Diferencia total: {diffTotal >= 0 ? '+' : ''}{fmtMXN(diffTotal)}
          </span>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: 16 }}>
            ✕
          </button>
        </div>
      </div>

      {/* Totales */}
      <div style={{
        display: 'flex', background: '#F8F8FF',
        borderBottom: '1px solid #E8E6E0',
      }}>
        <div style={{ flex: 1, padding: '10px 16px', borderRight: '1px solid #E8E6E0' }}>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 2 }}>v{vA?.version_number} · {vA?.created_at}</div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtMXN(diff.total_a)}</div>
        </div>
        <div style={{ flex: 1, padding: '10px 16px' }}>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 2 }}>v{vB?.version_number} · {vB?.created_at}</div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtMXN(diff.total_b)}</div>
        </div>
      </div>

      {/* Cambios */}
      <div style={{ padding: '12px 16px' }}>
        {diff.changes.length === 0 ? (
          <div className="text-center text-muted py-2" style={{ fontSize: 13 }}>
            No hay cambios en los conceptos entre estas versiones
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {diff.changes.map((c, i) => {
              const cfg = changeTypeConfig[c.type];
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px',
                  background: cfg.bg,
                  borderRadius: 6,
                  borderLeft: `3px solid ${cfg.color}`,
                }}>
                  <span style={{ fontWeight: 700, color: cfg.color, fontSize: 16, width: 20, textAlign: 'center' }}>
                    {cfg.arrow}
                  </span>
                  <div style={{ flex: 1, fontSize: 13 }}>
                    <span style={{ fontWeight: 600 }}>{c.name}</span>
                    {c.type === 'modified' && (
                      <span className="ml-2" style={{ color: '#6B7280', fontSize: 12 }}>
                        {fmtMXN(c.amount_a)} → {fmtMXN(c.amount_b)}
                        {c.visible_a !== c.visible_b && (
                          <span className="ml-2">
                            {c.visible_b ? '(ahora visible)' : '(ahora oculto)'}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <span style={{ fontWeight: 700, color: cfg.color, fontSize: 13, flexShrink: 0 }}>
                    {c.type === 'added'   && `+${fmtMXN(c.amount_b)}`}
                    {c.type === 'removed' && `-${fmtMXN(c.amount_a)}`}
                    {c.type === 'modified' && (
                      (c.amount_b - c.amount_a) >= 0
                        ? `+${fmtMXN(c.amount_b - c.amount_a)}`
                        : fmtMXN(c.amount_b - c.amount_a)
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetVersions;